package com.islamicmedia.shared.data.api

import io.ktor.client.HttpClient
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.request.request
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import io.ktor.client.statement.bodyAsText
import io.ktor.client.statement.readRawBytes
import io.ktor.http.ContentType
import kotlinx.coroutines.flow.emitAll
import io.ktor.http.HttpMethod
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import com.islamicmedia.shared.domain.model.AlternativesResult
import com.islamicmedia.shared.domain.model.Comment
import com.islamicmedia.shared.domain.model.CommentsResult
import com.islamicmedia.shared.domain.model.DisambiguationItem
import com.islamicmedia.shared.domain.model.Entitlement
import com.islamicmedia.shared.domain.model.KIDS_NOT_ALLOWED_CODE
import com.islamicmedia.shared.domain.model.KidsAlternative
import com.islamicmedia.shared.domain.model.LimitRequestCopy
import com.islamicmedia.shared.domain.model.LimitRequestResult
import com.islamicmedia.shared.domain.model.MediaPrefsResult
import com.islamicmedia.shared.domain.model.PricingConfig
import com.islamicmedia.shared.domain.model.ReferralAttachResult
import com.islamicmedia.shared.domain.model.ReferralInfo
import com.islamicmedia.shared.domain.model.ReferralRedeemResult
import com.islamicmedia.shared.util.ReferralCodeFormat
import kotlinx.coroutines.flow.Flow
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/** Result from disambiguate endpoint: items + optional journey tracking ID. */
data class DisambiguateResult(
    val items: List<DisambiguationItem>,
    val journeyId: String? = null
)

/** /api/quran/resolve wire types — verified Uthmani text keyed by the input reference string. */
@Serializable
private data class QuranResolveRequestDto(val references: List<String>)
@Serializable
data class QuranVerseDto(val arabic: String, val reference: String)
@Serializable
private data class QuranResolveResponseDto(val verses: Map<String, QuranVerseDto> = emptyMap())

/** Comments/reactions wire request types — field names must match the server's snake_case DTOs. */
@Serializable
private data class PostCommentRequestDto(
    val title: String,
    @SerialName("media_type") val mediaType: String,
    val year: String? = null,
    val info: String? = null,
    val body: String,
    @SerialName("first_name") val firstName: String
)
@Serializable
private data class EditCommentRequestDto(val body: String)
@Serializable
private data class MediaPrefRequestDto(
    val title: String,
    @SerialName("media_type") val mediaType: String,
    val pinned: Boolean? = null,
    val hidden: Boolean? = null
)
@Serializable
private data class ReactionRequestDto(
    val title: String,
    @SerialName("media_type") val mediaType: String,
    val year: String? = null,
    val info: String? = null,
    val reaction: Int
)


/**
 * A ready-to-use client with the default [createHttpClient]/[serverBaseUrl]. Lets a caller (e.g. a
 * standalone Kobweb page with no ViewModel) get an [AnthropicApiClient] without itself depending on
 * ktor-client's `HttpClient` type — some consuming modules (like `:site`) only have ktor-client on
 * their classpath transitively, which is enough to call this function but not enough to reference
 * `HttpClient` directly.
 */
fun createDefaultApiClient(): AnthropicApiClient = AnthropicApiClient(createHttpClient(), serverBaseUrl())

/** The server makes a clip in a few seconds (two tries at most); past this the device voice reads it. */
private const val KIDS_SPEECH_TIMEOUT_MS = 25_000L

class AnthropicApiClient(
    private val client: HttpClient,
    private val baseUrl: String = "http://localhost:8081"
) {
    private val quranJson = Json { ignoreUnknownKeys = true }

    /** Lenient on purpose: the billing summary gains fields over time, and a shipped app
     *  must not start failing to read it because the server learned something new. */
    private val billingJson = Json { ignoreUnknownKeys = true }

    // ── V2 endpoints (server-side pipeline) ─────────────────

    /**
     * V2: Calls /api/disambiguate on the server. Server handles Perplexity call.
     */
    private fun getAuthToken(): String? = try {
        SupabaseAuthService.instance.accessToken
    } catch (_: Exception) { null }

    /** Get a fresh token by forcing a session refresh. Use before important API calls. */
    private suspend fun getFreshAuthToken(): String? = try {
        SupabaseAuthService.instance.getFreshAccessToken()
    } catch (_: Exception) {
        getAuthToken() // Fallback to cached token
    }

    /**
     * A plain authenticated call to one of the server's JSON endpoints: (HTTP status, body text), or
     * null when the server couldn't be reached. Sends the session token, and on a 401 retries once with
     * a genuinely refreshed one (the same rule as the cached-result read). For endpoints that need no
     * special handling of the response, such as bulk scan.
     */
    suspend fun authedCall(method: HttpMethod, path: String, body: String? = null): Pair<Int, String>? {
        suspend fun send(token: String?) = client.request("$baseUrl$path") {
            this.method = method
            token?.let { header("Authorization", "Bearer $it") }
            if (body != null) {
                contentType(ContentType.Application.Json)
                setBody(body)
            }
        }
        return try {
            val token = getAuthToken()
            var response = send(token)
            if (response.status.value == 401) {
                val fresh = getFreshAuthToken()
                if (fresh != null && fresh != token) response = send(fresh)
            }
            response.status.value to response.bodyAsText()
        } catch (e: Exception) {
            null
        }
    }

    /**
     * Streams a bulk spine scan: the server sends each book as soon as it is identified. Uses a freshly
     * refreshed token, since this opens one long request that can't retry a 401 half-way through.
     */
    fun startSpineScanSse(requestBody: String): kotlinx.coroutines.flow.Flow<SseEvent> = kotlinx.coroutines.flow.flow {
        val token = getFreshAuthToken() ?: getAuthToken()
        emitAll(createSseFlow(url = "$baseUrl/api/extract-spines?stream=1", body = requestBody, authToken = token))
    }

    suspend fun postDisambiguate(query: String): DisambiguateResult {
        val body = """{"query":"${query.replace("\"", "\\\"")}"}"""

        // Try with current token — but allow unauthenticated requests
        // (server auth is relaxed, search should work without login)
        var token = getAuthToken()
        if (token == null) {
            println("[AUTH] No token available, attempting session refresh...")
            token = getFreshAuthToken()
        }
        if (token == null) {
            println("[AUTH] Proceeding without auth token (server allows unauthenticated requests)")
        }

        var response = try {
            client.post("$baseUrl/api/disambiguate") {
                contentType(ContentType.Application.Json)
                token?.let { header("Authorization", "Bearer $it") }
                setBody(body)
            }
        } catch (e: Exception) {
            throw Exception("Cannot connect to server. Please try again in a moment.")
        }

        // If 401, refresh token and retry once
        if (response.status.value == 401) {
            println("[AUTH] Token rejected, refreshing...")
            token = getFreshAuthToken()
            if (token == null) {
                throw Exception("401 Your session has expired. Please sign out and sign back in.")
            }
            response = try {
                client.post("$baseUrl/api/disambiguate") {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $token")
                    setBody(body)
                }
            } catch (e: Exception) {
                throw Exception("Cannot connect to server. Please try again in a moment.")
            }
        }

        val responseText = response.bodyAsText()
        if (!response.status.isSuccess()) {
            throw Exception("Server error (${response.status}): $responseText")
        }

        // Server returns items as a plain JSON array (backwards compatible).
        // JourneyId is passed via X-Journey-Id response header.
        val journeyId = response.headers["X-Journey-Id"]
        val items = json.decodeFromString<List<DisambiguationItem>>(responseText)
        return DisambiguateResult(items = items, journeyId = journeyId)
    }

    /**
     * V2: Connects to /api/analyze SSE stream. Server runs the full pipeline
     * and streams progress events back in real-time.
     * Uses platform-specific streaming (OkHttp on Android, NSURLSession on iOS, Fetch on JS).
     */
    fun startAnalysisSse(requestBody: String): Flow<SseEvent> {
        // Use the freshest token available (getFreshAuthToken is suspend, can't call from non-suspend)
        // The token was already refreshed during postDisambiguate which always runs first
        val version = try { "${com.islamicmedia.shared.util.appVersion} (${com.islamicmedia.shared.util.appBuildNumber})" } catch (_: Exception) { null }
        val deviceToken = try { com.islamicmedia.shared.platform.getPushToken() } catch (_: Exception) { null }
        return createSseFlow(
            url = "$baseUrl/api/analyze",
            body = requestBody,
            authToken = getAuthToken(),
            extraHeaders = buildMap {
                version?.let { put("X-App-Version", it) }
                deviceToken?.let { put("X-Device-Token", it) }
            }
        )
    }

    /**
     * V2: Reconnect to an already-running analysis pipeline by journeyId. Used to recover from
     * an SSE drop (typically because the OS suspended the app) without starting a fresh pipeline.
     * Server returns 410 Gone if the pipeline isn't in-flight; the SSE flow will surface that as
     * an `error` event with message `"not_in_flight"`, and the caller should fall back to
     * /api/cached-result.
     */
    fun resumeAnalysisSse(journeyId: String, kids: Boolean = false): Flow<SseEvent> {
        val version = try { "${com.islamicmedia.shared.util.appVersion} (${com.islamicmedia.shared.util.appBuildNumber})" } catch (_: Exception) { null }
        val deviceToken = try { com.islamicmedia.shared.platform.getPushToken() } catch (_: Exception) { null }
        // kids = the server rewrites the replayed stream to kid-safe frames (see KidsStreamFilter).
        val body = if (kids) """{"journeyId":"$journeyId","kids":true}""" else """{"journeyId":"$journeyId"}"""
        return createSseFlow(
            url = "$baseUrl/api/resume",
            body = body,
            authToken = getAuthToken(),
            extraHeaders = buildMap {
                version?.let { put("X-App-Version", it) }
                deviceToken?.let { put("X-Device-Token", it) }
            }
        )
    }

    /** Call this before starting an analysis flow to ensure token is fresh. Throws if no session. */
    suspend fun refreshTokenIfNeeded() {
        val token = getFreshAuthToken()
        if (token == null) {
            throw Exception("401 Your session has expired. Please sign out and sign back in.")
        }
    }

    // ── Device Token Registration (Push Notifications) ──────

    suspend fun registerDeviceToken(token: String, platform: String): Boolean {
        return try {
            val authToken = getAuthToken()
            // Include email in body as fallback in case auth token is expired
            val email = try {
                com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.currentUser?.email
            } catch (_: Exception) { null }
            val emailField = if (email != null) ""","email":"$email"""" else ""
            val body = """{"token":"$token","platform":"$platform"$emailField}"""
            val response = client.post("$baseUrl/api/device-token") {
                contentType(ContentType.Application.Json)
                authToken?.let { header("Authorization", "Bearer $it") }
                setBody(body)
            }
            response.status.isSuccess()
        } catch (e: Exception) {
            println("[FCM] Failed to register device token: ${e.message}")
            false
        }
    }

    // ── Account ──────────────────────────────────────────────

    /**
     * Fetch the authenticated user's account expiry date (ISO timestamp string).
     * Returns null if the user has no expiry set, no auth, or any network error —
     * the profile screen falls back to hiding the row in that case.
     */
    /**
     * Best-effort cover lookup by title for history re-hydration. Unauthenticated;
     * returns the server-resolved poster URL or null. Never throws.
     */
    suspend fun fetchCoverUrl(title: String, mediaType: String, year: String? = null): String? {
        return try {
            val response = client.get("$baseUrl/api/cover") {
                parameter("title", title)
                parameter("mediaType", mediaType)
                if (!year.isNullOrBlank()) parameter("year", year)
            }
            if (!response.status.isSuccess()) return null
            val obj = json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject
                ?: return null
            val prim = obj["coverUrl"] as? kotlinx.serialization.json.JsonPrimitive
            if (prim == null || prim.toString() == "null") null
            else prim.content.takeIf { it.isNotBlank() }
        } catch (_: Exception) {
            null
        }
    }

    /**
     * Fetch the bundled autocomplete catalog (GET /api/titles/index). Unauthenticated + cacheable.
     * Returns null on any failure so the caller keeps using the locally-cached copy. Never throws.
     */
    suspend fun getTitlesIndex(): List<DisambiguationItem>? {
        return try {
            val response = client.get("$baseUrl/api/titles/index")
            if (!response.status.isSuccess()) return null
            json.decodeFromString<List<DisambiguationItem>>(response.bodyAsText())
        } catch (_: Exception) {
            null
        }
    }

    /**
     * Admin-controlled paywall pricing (GET /api/pricing). Public, cacheable, no auth. Returns null
     * on any failure or when nothing is configured yet — the caller falls back to
     * [PricingConfig.DEFAULT] so the paywall always has plans to show. Never throws.
     */
    suspend fun fetchPricingConfig(): PricingConfig? {
        return try {
            val response = client.get("$baseUrl/api/pricing")
            if (!response.status.isSuccess()) return null
            json.decodeFromString<PricingConfig>(response.bodyAsText())
        } catch (_: Exception) {
            null
        }
    }

    /**
     * Live "more like this" for the result screen (GET /api/alternatives). Unauthenticated;
     * returns green, same-media_type recommendations for [title]. Returns null on any failure
     * (caller renders nothing). Never throws.
     */
    suspend fun fetchAlternatives(title: String, mediaType: String, year: String? = null, author: String? = null): AlternativesResult? {
        return try {
            // Optional token: lets the server identify the user for the "alternatives ready" push
            // when it kicks off background discovery. Absence just means no notification.
            val token = getAuthToken()
            val response = client.get("$baseUrl/api/alternatives") {
                parameter("title", title)
                parameter("mediaType", mediaType)
                if (!year.isNullOrBlank()) parameter("year", year)
                if (!author.isNullOrBlank()) parameter("author", author)
                token?.let { header("Authorization", "Bearer $it") }
            }
            if (!response.status.isSuccess()) return null
            json.decodeFromString<AlternativesResult>(response.bodyAsText())
        } catch (_: Exception) {
            null
        }
    }

    // ── Comments & Reactions (shared, public thread + thumbs up/down per title) ─

    /** GET /api/comments — the shared thread + reaction tally for a title. Unauthenticated reads
     *  are fine; an optional token just resolves isMine/myReaction. Null on any failure. */
    suspend fun fetchComments(title: String, mediaType: String, year: String? = null): CommentsResult? {
        return try {
            val token = getAuthToken()
            val response = client.get("$baseUrl/api/comments") {
                parameter("title", title)
                parameter("mediaType", mediaType)
                if (!year.isNullOrBlank()) parameter("year", year)
                token?.let { header("Authorization", "Bearer $it") }
            }
            if (!response.status.isSuccess()) return null
            json.decodeFromString<CommentsResult>(response.bodyAsText())
        } catch (_: Exception) { null }
    }

    /** POST /api/comments — auth required. [firstName] is the signed-in user's own first name,
     *  snapshotted client-side at post time. Returns the created comment, or null on failure. */
    suspend fun postComment(title: String, mediaType: String, year: String?, body: String, firstName: String): Comment? {
        return try {
            val token = getFreshAuthToken() ?: return null
            val requestBody = json.encodeToString(PostCommentRequestDto(title, mediaType, year, body = body, firstName = firstName))
            val response = client.post("$baseUrl/api/comments") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(requestBody)
            }
            if (!response.status.isSuccess()) return null
            json.decodeFromString<Comment>(response.bodyAsText())
        } catch (_: Exception) { null }
    }

    /** PATCH /api/comments/{id} — auth required; server 403s unless the caller owns the comment. */
    suspend fun editComment(commentId: String, body: String): Boolean {
        return try {
            val token = getFreshAuthToken() ?: return false
            val response = client.patch("$baseUrl/api/comments/$commentId") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(json.encodeToString(EditCommentRequestDto(body)))
            }
            response.status.isSuccess()
        } catch (_: Exception) { false }
    }

    /** DELETE /api/comments/{id} — auth required; server 403s unless the caller owns the comment. */
    suspend fun deleteComment(commentId: String): Boolean {
        return try {
            val token = getFreshAuthToken() ?: return false
            val response = client.delete("$baseUrl/api/comments/$commentId") {
                header("Authorization", "Bearer $token")
            }
            response.status.isSuccess()
        } catch (_: Exception) { false }
    }

    /** POST /api/reactions — auth required. [reaction] is 1 (like) or -1 (dislike); tapping the
     *  same one again removes it, server-side and atomically. Returns the fresh tally (comments
     *  left empty — callers only read the count/myReaction fields), or null on failure. */
    suspend fun setReaction(title: String, mediaType: String, year: String?, reaction: Int): CommentsResult? {
        return try {
            val token = getFreshAuthToken() ?: return null
            val requestBody = json.encodeToString(ReactionRequestDto(title, mediaType, year, reaction = reaction))
            val response = client.post("$baseUrl/api/reactions") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(requestBody)
            }
            if (!response.status.isSuccess()) return null
            json.decodeFromString<CommentsResult>(response.bodyAsText())
        } catch (_: Exception) { null }
    }

    // ── Your Analyses pin / hide prefs ─────────────────────────

    /** GET /api/media-prefs — the caller's pin/hide state for every title. Auth required
     *  (these are per-account); null on failure so the list just renders unfiltered. */
    suspend fun fetchMediaPrefs(): MediaPrefsResult? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.get("$baseUrl/api/media-prefs") {
                header("Authorization", "Bearer $token")
            }
            if (!response.status.isSuccess()) return null
            json.decodeFromString<MediaPrefsResult>(response.bodyAsText())
        } catch (_: Exception) { null }
    }

    /** POST /api/media-prefs — set pin and/or hide for one title. Pass null to leave a field
     *  untouched, so the two toggles never clobber each other. */
    suspend fun setMediaPref(title: String, mediaType: String, pinned: Boolean? = null, hidden: Boolean? = null): Boolean {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return false
            val response = client.post("$baseUrl/api/media-prefs") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(json.encodeToString(MediaPrefRequestDto(title, mediaType, pinned, hidden)))
            }
            response.status.isSuccess()
        } catch (_: Exception) { false }
    }

    // ── Kids Mode forgot-PIN ────────────────────────────────

    /** Emails a 6-digit code to the signed-in account. Returns the token to send back with it, or null. */
    suspend fun requestKidsPinReset(): String? {
        return try {
            val token = getFreshAuthToken() ?: getAuthToken() ?: return null
            val response = client.post("$baseUrl/api/kids/pin-reset/request") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody("{}")
            }
            if (!response.status.isSuccess()) return null
            json.parseToJsonElement(response.bodyAsText()).jsonObject["token"]?.jsonPrimitive?.contentOrNull
        } catch (_: Exception) { null }
    }

    /** True only when the server confirms [code] matches the emailed one for this account. */
    suspend fun verifyKidsPinReset(resetToken: String, code: String): Boolean {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return false
            val response = client.post("$baseUrl/api/kids/pin-reset/verify") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(buildJsonObject {
                    put("token", JsonPrimitive(resetToken))
                    put("code", JsonPrimitive(code))
                }.toString())
            }
            response.status.isSuccess() &&
                json.parseToJsonElement(response.bodyAsText()).jsonObject["ok"]?.jsonPrimitive?.contentOrNull == "true"
        } catch (_: Exception) { false }
    }

    /**
     * KIDZONE 8 and over, on a NO: up to four similar titles that would be a clear YES (GET /api/kids/alternatives).
     * Only names and pictures come back. Empty on any failure: the section just doesn't show.
     */
    suspend fun fetchKidsAlternatives(title: String, mediaType: String, year: String?): List<KidsAlternative> {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return emptyList()
            val response = client.get("$baseUrl/api/kids/alternatives") {
                header("Authorization", "Bearer $token")
                parameter("title", title)
                parameter("mediaType", mediaType)
                if (!year.isNullOrBlank()) parameter("year", year)
            }
            if (!response.status.isSuccess()) return emptyList()
            val items = json.parseToJsonElement(response.bodyAsText()).jsonObject["items"] ?: return emptyList()
            json.decodeFromJsonElement(kotlinx.serialization.builtins.ListSerializer(KidsAlternative.serializer()), items)
        } catch (_: Exception) { emptyList() }
    }

    /**
     * KIDZONE "Read it to me": [text] read out in the KIDZONE voice, as a WAV file (POST /api/kids/speech).
     * Null when there is no clip (204, switched off, slow, any failure): the caller reads it with the device voice.
     */
    suspend fun fetchKidsSpeech(text: String): ByteArray? {
        return try {
            kotlinx.coroutines.withTimeoutOrNull(KIDS_SPEECH_TIMEOUT_MS) {
                val token = getAuthToken() ?: getFreshAuthToken() ?: return@withTimeoutOrNull null
                val response = client.post("$baseUrl/api/kids/speech") {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $token")
                    setBody(buildJsonObject { put("text", JsonPrimitive(text)) }.toString())
                }
                if (response.status != io.ktor.http.HttpStatusCode.OK) return@withTimeoutOrNull null
                response.readRawBytes().takeIf { it.size > 44 }
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (_: Exception) { null }
    }

    suspend fun fetchAccountExpiry(): String? {
        return try {
            val token = getFreshAuthToken() ?: return null
            val response = client.get("$baseUrl/api/account/expiry") {
                header("Authorization", "Bearer $token")
            }
            if (!response.status.isSuccess()) return null
            val body = response.bodyAsText()
            // Tiny JSON: {"expiry_date":"2027-..."} or {"expiry_date":null}
            val obj = json.parseToJsonElement(body) as? kotlinx.serialization.json.JsonObject
                ?: return null
            val prim = obj["expiry_date"] as? kotlinx.serialization.json.JsonPrimitive
            if (prim == null || prim.toString() == "null") null else prim.content
        } catch (_: Exception) {
            null
        }
    }

    // ── Subscription billing (see PRD_SUBSCRIPTION_BILLING.md) ──────────────

    /** Fetch the user's entitlement (credit balances / plan / status), or null on failure. */
    suspend fun fetchEntitlement(): Entitlement? {
        return try {
            // Use the cached token (don't force a session refresh on every call — that emits
            // auth-session events and can trigger refresh storms / churn loops). Supabase
            // auto-refreshes in the background; the server tolerates a slightly-stale token.
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.get("$baseUrl/api/account/entitlement") {
                header("Authorization", "Bearer $token")
            }
            if (!response.status.isSuccess()) return null
            json.decodeFromString(Entitlement.serializer(), response.bodyAsText())
        } catch (_: Exception) { null }
    }

    /**
     * Redeem a promo code. Returns the server's verdict string — one of
     * `ok | not_found | disabled | expired | already_redeemed | already_subscribed` — or
     * `error` when the call itself failed (offline, 500, unparseable).
     *
     * Note "not signed in" can't reach the server: the endpoint is auth-gated, and the UI only
     * offers the voucher entry to a signed-in user in the first place.
     */
    suspend fun redeemVoucher(code: String): Pair<String, Int?> {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return "error" to null
            val response = client.post("$baseUrl/api/voucher/redeem") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody("""{"code":"${code.trim().replace("\\", "").replace("\"", "")}"}""")
            }
            // 429 from the rate limiter is its own outcome — the caller shows "too many attempts"
            // rather than "invalid code", which would otherwise read as the code being wrong.
            if (response.status.value == 429) return "rate_limited" to null
            if (!response.status.isSuccess()) return "error" to null
            val body = json.parseToJsonElement(response.bodyAsText()).jsonObject
            val status = body["status"]?.jsonPrimitive?.contentOrNull ?: "error"
            status to body["days"]?.jsonPrimitive?.contentOrNull?.toIntOrNull()
        } catch (e: Exception) {
            println("[VOUCHER] redeem failed: ${e.message}")
            "error" to null
        }
    }

    // ── Request additional searches (see server/.../routes/LimitRequestRoutes.kt) ───────────────

    /**
     * POST /api/limit-request: a signed-in person stopped by the WEEKLY limit asks for more
     * searches. [reason] is what they typed; the server trims and checks it (10 to 1000 characters),
     * saves it, and emails support with the person's address as the reply address.
     *
     * 200 -> [LimitRequestResult.Sent]. 429 `already_requested` -> [LimitRequestResult.AlreadyRequested]
     * (one request per 24 hours). 400 `reason_length` -> [LimitRequestResult.Invalid]. Anything else
     * (offline, 401, 503, the hourly limiter) -> [LimitRequestResult.Failed], with the server's own
     * `message` when it sent one. Never throws.
     */
    suspend fun requestMoreSearches(reason: String): LimitRequestResult {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return LimitRequestResult.Failed()
            val version = try { "${com.islamicmedia.shared.util.appVersion} (${com.islamicmedia.shared.util.appBuildNumber})" } catch (_: Exception) { null }
            val response = client.post("$baseUrl/api/limit-request") {
                header("Authorization", "Bearer $token")
                version?.let { header("X-App-Version", it) }
                contentType(ContentType.Application.Json)
                setBody(buildJsonObject { put("reason", JsonPrimitive(reason)) }.toString())
            }
            if (response.status.isSuccess()) return LimitRequestResult.Sent
            val body = try { json.parseToJsonElement(response.bodyAsText()).jsonObject } catch (_: Exception) { null }
            val code = body?.get("error")?.let { (it as? JsonPrimitive)?.contentOrNull }
            val message = body?.get("message")?.let { (it as? JsonPrimitive)?.contentOrNull }?.takeIf { it.isNotBlank() }
            println("[LIMIT-REQUEST] ${response.status} code=$code")
            when {
                code == "already_requested" -> LimitRequestResult.AlreadyRequested(message ?: LimitRequestCopy.ALREADY_REQUESTED)
                code == "reason_length" -> LimitRequestResult.Invalid(message ?: LimitRequestCopy.TOO_SHORT)
                response.status.value == 429 -> LimitRequestResult.Failed(message ?: LimitRequestCopy.TOO_MANY)
                else -> LimitRequestResult.Failed(message)
            }
        } catch (e: Exception) {
            println("[LIMIT-REQUEST] failed: ${e.message}")
            LimitRequestResult.Failed()
        }
    }

    // ── Referrals (see server/.../routes/ReferralRoutes.kt) ─────────────────────────────────────

    /**
     * GET /api/referral/me: the signed-in member's code, share link and counts. The answer's
     * status is "ok", "not_eligible" or "disabled" (see [ReferralInfo]). Null when the call failed
     * (offline, not signed in, 429, 500), which the screen shows as a retry.
     */
    suspend fun fetchReferral(): ReferralInfo? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.get("$baseUrl/api/referral/me") {
                header("Authorization", "Bearer $token")
            }
            if (!response.status.isSuccess()) {
                println("[REFERRAL] me: ${response.status}")
                return null
            }
            billingJson.decodeFromString(ReferralInfo.serializer(), response.bodyAsText())
        } catch (e: Exception) {
            println("[REFERRAL] me failed: ${e.message}")
            null
        }
    }

    /**
     * GET /api/referral/code/{code}: is this code live? No auth; the server limits it per IP.
     * Returns "ok" | "not_found" | "referrer_ineligible", or "rate_limited" / "error" made up here.
     * Anything that can't be a code answers "not_found" without a request.
     */
    suspend fun previewReferral(code: String): String {
        // Canonical codes are 8 characters of [0-9A-Z], so the path needs no encoding.
        val canonical = ReferralCodeFormat.normalize(code) ?: return "not_found"
        return try {
            val response = client.get("$baseUrl/api/referral/code/$canonical")
            if (response.status.value == 429) return "rate_limited"
            if (!response.status.isSuccess()) return "error"
            val body = json.parseToJsonElement(response.bodyAsText()).jsonObject
            body["status"]?.jsonPrimitive?.contentOrNull ?: "error"
        } catch (e: Exception) {
            println("[REFERRAL] preview failed: ${e.message}")
            "error"
        }
    }

    /**
     * POST /api/referral/attach: tie a friend's code to the signed-in account, so their checkout
     * carries it. [source] is "link" (a code captured from a /r/ link) or "typed_paywall".
     *
     * Every server verdict comes back 200 with a status (see [ReferralAttachResult]); only the
     * rate limiter answers 429, which becomes "rate_limited" so the dialog can say "too many
     * tries" instead of implying the code is wrong. Anything that can't be a code answers
     * "not_found" without a request.
     */
    suspend fun attachReferral(code: String, source: String): ReferralAttachResult {
        val canonical = ReferralCodeFormat.normalize(code) ?: return ReferralAttachResult("not_found")
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return ReferralAttachResult("error")
            val response = client.post("$baseUrl/api/referral/attach") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(buildJsonObject {
                    put("code", JsonPrimitive(canonical))
                    put("source", JsonPrimitive(source))
                }.toString())
            }
            if (response.status.value == 429) return ReferralAttachResult("rate_limited")
            if (!response.status.isSuccess()) {
                println("[REFERRAL] attach: ${response.status}")
                return ReferralAttachResult("error")
            }
            val body = json.parseToJsonElement(response.bodyAsText()).jsonObject
            ReferralAttachResult(
                status = body["status"]?.jsonPrimitive?.contentOrNull ?: "error",
                code = body["code"]?.jsonPrimitive?.contentOrNull,
            )
        } catch (e: Exception) {
            println("[REFERRAL] attach failed: ${e.message}")
            ReferralAttachResult("error")
        }
    }

    /**
     * POST /api/referral/redeem: enter a friend's code AFTER subscribing (the Free Gift screen's
     * "Got a code from a friend?", only offered while [ReferralInfo.canRedeem]). [code] is what the
     * member typed; null or blank uses whatever code is already attached to the account (from the
     * paywall dialog), which is why this doesn't short-circuit on a blank string the way
     * [attachReferral] does on an unparseable one.
     *
     * Every server verdict comes back 200 with a status (see [ReferralRedeemResult]); only the rate
     * limiter answers 429, which becomes "rate_limited". A typed [code] that can't be a code at all
     * answers "not_found" without a request.
     */
    suspend fun redeemReferral(code: String?): ReferralRedeemResult {
        val typed = code?.takeIf { it.isNotBlank() }
        val canonical = typed?.let { ReferralCodeFormat.normalize(it) }
        if (typed != null && canonical == null) return ReferralRedeemResult("not_found")
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return ReferralRedeemResult("error")
            val response = client.post("$baseUrl/api/referral/redeem") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(buildJsonObject {
                    canonical?.let { put("code", JsonPrimitive(it)) }
                }.toString())
            }
            if (response.status.value == 429) return ReferralRedeemResult("rate_limited")
            if (!response.status.isSuccess()) {
                println("[REFERRAL] redeem: ${response.status}")
                return ReferralRedeemResult("error")
            }
            val body = json.parseToJsonElement(response.bodyAsText()).jsonObject
            ReferralRedeemResult(
                status = body["status"]?.jsonPrimitive?.contentOrNull ?: "error",
                nextBill = body["next_bill"]?.jsonPrimitive?.contentOrNull,
                code = body["code"]?.jsonPrimitive?.contentOrNull,
            )
        } catch (e: Exception) {
            println("[REFERRAL] redeem failed: ${e.message}")
            ReferralRedeemResult("error")
        }
    }

    /**
     * Start a checkout for [planKey] ("basic_10" | "pro_30"). Returns the URL to open in a
     * browser (real Stripe Checkout, or the dev-mock success redirect). Null on failure.
     *
     * [referralCode] is optional: a friend's code typed at the paywall. The server attaches it
     * first and then uses whatever code is attached to the account, so it can be left out once
     * [attachReferral] has said "ok". Sent only when it normalises to a real code.
     */
    suspend fun createCheckout(planKey: String, successUrl: String? = null, referralCode: String? = null): String? {
        return try {
            // Prefer cached token (same pattern as fetchEntitlement) to avoid Supabase-KMP
            // clearing the local session when a forced refresh unexpectedly fails.
            val token = getAuthToken() ?: getFreshAuthToken()
            if (token == null) {
                println("[BILLING] createCheckout: no auth token")
                return null
            }
            val body = buildString {
                append("{\"plan\":\"$planKey\"")
                if (successUrl != null) append(",\"successUrl\":\"$successUrl\"")
                // Canonical codes are [0-9A-Z] only, so nothing here needs escaping.
                ReferralCodeFormat.normalize(referralCode)?.let { append(",\"referralCode\":\"$it\"") }
                append("}")
            }
            val response = client.post("$baseUrl/api/checkout") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(body)
            }
            if (!response.status.isSuccess()) {
                println("[BILLING] createCheckout: ${response.status} — ${response.bodyAsText().take(200)}")
                return null
            }
            (json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject)
                ?.get("url")?.jsonPrimitive?.contentOrNull
        } catch (e: Exception) {
            println("[BILLING] createCheckout exception: $e")
            null
        }
    }

    /**
     * Attach a purchase made on the public offer page to the CURRENTLY SIGNED-IN account
     * (POST /api/claim-purchase). Requires auth — the destination email is taken from the token
     * server-side, never sent from here.
     *
     * Returns the raw status: "ok" (attached/transferred), "pending" (Stripe webhook still in
     * flight — retry), or an error status. Null on transport failure.
     */
    suspend fun claimPublicPurchase(sessionId: String): String? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken()
            if (token == null) {
                println("[BILLING] claimPublicPurchase: no auth token")
                return null
            }
            val response = client.post("$baseUrl/api/claim-purchase") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody("{\"sessionId\":\"${sessionId.replace("\"", "")}\"}")
            }
            val bodyText = response.bodyAsText()
            if (!response.status.isSuccess()) {
                println("[BILLING] claimPublicPurchase: ${response.status} — ${bodyText.take(200)}")
                return null
            }
            (json.parseToJsonElement(bodyText) as? kotlinx.serialization.json.JsonObject)
                ?.get("status")?.jsonPrimitive?.contentOrNull
        } catch (e: Exception) {
            println("[BILLING] claimPublicPurchase exception: $e")
            null
        }
    }

    /** Get a Stripe Customer Portal URL to manage/cancel the subscription. Null if none. */
    suspend fun fetchBillingPortalUrl(returnUrl: String? = null): String? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val body = if (returnUrl != null) "{\"returnUrl\":\"$returnUrl\"}" else "{}"
            val response = client.post("$baseUrl/api/billing-portal") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(body)
            }
            if (!response.status.isSuccess()) return null
            (json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject)
                ?.get("url")?.jsonPrimitive?.contentOrNull
        } catch (_: Exception) { null }
    }

    // ══════════════════════════════════════════════════════════════════════════════════════════
    // Cancellation flow. See server/.../routes/CancellationRoutes.kt.
    // ══════════════════════════════════════════════════════════════════════════════════════════

    /**
     * Open a cancellation journey. [reason] is a [com.islamicmedia.shared.domain.model.CancelReason]
     * key; [note] is only sent for "other".
     *
     * Returns the flow id, the offers this person is actually eligible for (already ordered, and
     * with anything they've taken recently removed), and when their access would end. Null when the
     * server says they can't cancel in-app at all — which the UI treats as "fall back to the
     * billing portal" rather than as an error, since that path always works.
     */
    suspend fun cancelStart(
        reason: String?, note: String?, platform: String?
    ): com.islamicmedia.shared.domain.model.CancelFlowStart? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.post("$baseUrl/api/cancel/start") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(buildJsonObject {
                    if (reason != null) put("reason", JsonPrimitive(reason))
                    if (note != null) put("note", JsonPrimitive(note))
                    if (platform != null) put("platform", JsonPrimitive(platform))
                }.toString())
            }
            if (!response.status.isSuccess()) {
                println("[CANCEL] start: ${response.status} — ${response.bodyAsText().take(200)}")
                return null
            }
            val obj = json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject
                ?: return null
            // The server sends whole offer objects, not keys: it already applied the config, the
            // eligibility cooldown and the billing-interval guard, so the client renders exactly
            // what came back rather than re-deriving anything from its own copy of the config.
            val offers = (obj["offers"] as? kotlinx.serialization.json.JsonArray)
                ?.mapNotNull { el ->
                    runCatching {
                        billingJson.decodeFromJsonElement(
                            com.islamicmedia.shared.domain.model.RetentionOfferConfig.serializer(), el
                        )
                    }.getOrNull()
                } ?: emptyList()
            // The referral warning comes as a flag and its date; the date alone is what the flow
            // shows, and only when the flag says there is something to lose.
            val referralAtRisk = obj["referralFreeMonthAtRisk"]?.jsonPrimitive?.contentOrNull == "true"
            com.islamicmedia.shared.domain.model.CancelFlowStart(
                flowId = obj["flowId"]?.jsonPrimitive?.contentOrNull ?: "",
                offers = offers,
                periodEndIso = obj["periodEnd"]?.jsonPrimitive?.contentOrNull,
                referralFreeMonthUntilIso = obj["referralFreeMonthUntil"]?.jsonPrimitive?.contentOrNull
                    ?.takeIf { referralAtRisk && it.isNotBlank() },
            )
        } catch (e: Exception) {
            println("[CANCEL] start exception: $e")
            null
        }
    }

    /**
     * Record a decision on one offer. `accept` applies it in Stripe; `decline` only writes the
     * audit trail, so a failed decline is genuinely harmless and is not surfaced.
     *
     * Returns the new renewal/resume date on an accepted pause or free month, an empty string on
     * any other success, and null on failure.
     */
    suspend fun cancelOffer(flowId: String?, offer: String, accept: Boolean): String? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.post("$baseUrl/api/cancel/offer") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(buildJsonObject {
                    if (!flowId.isNullOrBlank()) put("flowId", JsonPrimitive(flowId))
                    put("offer", JsonPrimitive(offer))
                    put("action", JsonPrimitive(if (accept) "accept" else "decline"))
                }.toString())
            }
            if (!response.status.isSuccess()) {
                println("[CANCEL] offer $offer: ${response.status} — ${response.bodyAsText().take(200)}")
                return null
            }
            val obj = json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject
            obj?.get("date")?.jsonPrimitive?.contentOrNull ?: ""
        } catch (e: Exception) {
            println("[CANCEL] offer exception: $e")
            null
        }
    }

    /** Go through with it. Returns the ISO date their access ends, or null on failure. */
    suspend fun cancelConfirm(flowId: String?): String? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.post("$baseUrl/api/cancel/confirm") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(buildJsonObject {
                    if (!flowId.isNullOrBlank()) put("flowId", JsonPrimitive(flowId))
                }.toString())
            }
            if (!response.status.isSuccess()) {
                println("[CANCEL] confirm: ${response.status} — ${response.bodyAsText().take(200)}")
                return null
            }
            val obj = json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject
            obj?.get("endsAt")?.jsonPrimitive?.contentOrNull ?: ""
        } catch (e: Exception) {
            println("[CANCEL] confirm exception: $e")
            null
        }
    }

    /** End a pause early. Stripe bills immediately; access returns with the paid invoice. */
    suspend fun resumeSubscription(): Boolean {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return false
            client.post("$baseUrl/api/cancel/resume") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody("{}")
            }.status.isSuccess()
        } catch (e: Exception) {
            println("[CANCEL] resume exception: $e")
            false
        }
    }

    // ══════════════════════════════════════════════════════════════════════════════════════════
    // Self-serve account management — the in-app replacement for Stripe's hosted portal.
    // ══════════════════════════════════════════════════════════════════════════════════════════

    /** Card on file + recent invoices. Null on failure; an unmanageable member gets a summary
     *  with `manageable=false` rather than an error, which is a different thing. */
    suspend fun fetchBillingSummary(): com.islamicmedia.shared.domain.model.BillingSummary? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.get("$baseUrl/api/billing/summary") {
                header("Authorization", "Bearer $token")
            }
            if (!response.status.isSuccess()) return null
            billingJson.decodeFromString<com.islamicmedia.shared.domain.model.BillingSummary>(response.bodyAsText())
        } catch (e: Exception) {
            println("[BILLING] summary failed: $e")
            null
        }
    }

    /**
     * The admin-editable cancellation flow (GET /api/cancel-flow).
     *
     * Unauthenticated and cacheable: it is copy and ordering, nothing about the caller. Null on
     * any failure, which callers treat as "use the built-in defaults" rather than as an error —
     * a flow that refuses to open because a config fetch failed would be far worse than one
     * showing last release's wording.
     */
    suspend fun fetchCancelFlowConfig(): com.islamicmedia.shared.domain.model.CancelFlowConfig? {
        return try {
            val response = client.get("$baseUrl/api/cancel-flow")
            if (!response.status.isSuccess()) return null
            billingJson.decodeFromString<com.islamicmedia.shared.domain.model.CancelFlowConfig>(
                response.bodyAsText()
            )
        } catch (e: Exception) {
            println("[CANCEL] flow config fetch failed: $e")
            null
        }
    }

    /** Undo a scheduled cancellation. Returns the renewal date it goes back to. */
    suspend fun reactivateSubscription(): String? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.post("$baseUrl/api/billing/reactivate") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody("{}")
            }
            if (!response.status.isSuccess()) return null
            (json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject)
                ?.get("renewsAt")?.jsonPrimitive?.contentOrNull ?: ""
        } catch (e: Exception) {
            println("[BILLING] reactivate failed: $e")
            null
        }
    }

    /**
     * Begin a card change. Returns the SetupIntent client secret and the publishable key.
     *
     * These two together are what the card form needs, and neither is a secret in the dangerous
     * sense: the client secret can only confirm THIS one setup, and the publishable key is
     * designed to be shipped to browsers.
     */
    suspend fun createCardSetupIntent(): Pair<String, String>? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.post("$baseUrl/api/billing/setup-intent") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody("{}")
            }
            if (!response.status.isSuccess()) {
                println("[BILLING] setup-intent: ${response.status} — ${response.bodyAsText().take(150)}")
                return null
            }
            val obj = json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject
                ?: return null
            val secret = obj["clientSecret"]?.jsonPrimitive?.contentOrNull ?: return null
            val pk = obj["publishableKey"]?.jsonPrimitive?.contentOrNull ?: return null
            secret to pk
        } catch (e: Exception) {
            println("[BILLING] setup-intent failed: $e")
            null
        }
    }

    /** Finish a card change. The server re-verifies the intent against Stripe before trusting it. */
    suspend fun applyCardSetupIntent(setupIntentId: String): Boolean {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return false
            client.post("$baseUrl/api/billing/payment-method") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(buildJsonObject { put("setupIntentId", JsonPrimitive(setupIntentId)) }.toString())
            }.status.isSuccess()
        } catch (e: Exception) {
            println("[BILLING] apply card failed: $e")
            false
        }
    }

    /** Does this account accept marketing email? Null when we couldn't find out — the UI should
     *  hide the control rather than guess, since guessing wrong looks like a broken toggle. */
    suspend fun fetchEmailMarketing(): Boolean? {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return null
            val response = client.get("$baseUrl/api/account/email-preferences") {
                header("Authorization", "Bearer $token")
            }
            if (!response.status.isSuccess()) return null
            (json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject)
                ?.get("marketing")?.jsonPrimitive?.contentOrNull?.toBooleanStrictOrNull()
        } catch (e: Exception) {
            println("[NEWSLETTER] read preference failed: $e")
            null
        }
    }

    /** Opt in or out of marketing email. */
    suspend fun setEmailMarketing(marketing: Boolean): Boolean {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return false
            client.post("$baseUrl/api/account/email-preferences") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody("""{"marketing":$marketing}""")
            }.status.isSuccess()
        } catch (e: Exception) {
            println("[NEWSLETTER] write preference failed: $e")
            false
        }
    }

    /** Switch the active subscription to [planKey] ("pro_29" | "annual"); effective today and
     *  always prorated (Stripe credits the unused part of the current plan against the new one
     *  and charges only the difference now). Returns true on success. */
    suspend fun changePlan(planKey: String): Boolean {
        return try {
            val token = getAuthToken() ?: getFreshAuthToken() ?: return false
            val response = client.post("$baseUrl/api/change-plan") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody("{\"plan\":\"$planKey\"}")
            }
            response.status.isSuccess()
        } catch (_: Exception) { false }
    }

    /**
     * Fetch the authenticated user's dhikr high score. Returns 0 if no row yet, null if
     * the call fails (auth missing, network error, etc.) so the client can fall back to
     * a local-only score.
     */
    suspend fun fetchDhikrHighScore(): Int? {
        return try {
            val token = getFreshAuthToken() ?: return null
            val response = client.get("$baseUrl/api/account/dhikr") {
                header("Authorization", "Bearer $token")
            }
            if (!response.status.isSuccess()) return null
            val obj = json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject
                ?: return null
            (obj["high_score"] as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
        } catch (_: Exception) { null }
    }

    /**
     * Submit a new dhikr score. Server only persists if it strictly beats the existing
     * high score. Returns the resulting high score (which may be greater than [score] if
     * another device already wrote a higher one), or null on auth/network failure.
     */
    suspend fun updateDhikrHighScore(score: Int): Int? {
        return try {
            val token = getFreshAuthToken() ?: return null
            val response = client.post("$baseUrl/api/account/dhikr") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody("""{"score":$score}""")
            }
            if (!response.status.isSuccess()) return null
            val obj = json.parseToJsonElement(response.bodyAsText()) as? kotlinx.serialization.json.JsonObject
                ?: return null
            (obj["high_score"] as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
        } catch (_: Exception) { null }
    }

    // ── Cancel Analysis ──────────────────────────────────────

    /** Stop a running server-side pipeline. [cancelToken] is the handle every analysis has;
     *  [journeyId] is sent too when there is one, so a server that only knows the older field
     *  can still find the pipeline. */
    suspend fun cancelAnalysis(cancelToken: String?, journeyId: String? = null) {
        if (cancelToken == null && journeyId == null) return
        try {
            val fields = listOfNotNull(
                cancelToken?.let { """"cancelToken":"$it"""" },
                journeyId?.let { """"journeyId":"$it"""" }
            )
            client.post("$baseUrl/api/cancel") {
                contentType(ContentType.Application.Json)
                setBody("{${fields.joinToString(",")}}")
            }
            println("[CANCEL] Sent cancel request (token=$cancelToken journey=$journeyId)")
        } catch (e: Exception) {
            println("[CANCEL] Failed to cancel: ${e.message}")
        }
    }

    // ── Cached Result Fetch ──────────────────────────────────

    /**
     * Resolve cited Quran references to authoritative Uthmani text (server /api/quran/resolve).
     * Returns a map keyed by the EXACT input reference string; only verified references are
     * present — a reference absent from a SUCCESSFUL response could not be confirmed and its
     * section should be hidden. Used to correct OLD analyses whose stored Arabic predates
     * server-side enrichment. Returns null on transport failure (distinct from an empty success),
     * so the caller can leave existing text untouched rather than blanking valid-looking content.
     */
    suspend fun resolveQuranReferences(references: List<String>): Map<String, QuranVerseDto>? {
        if (references.isEmpty()) return emptyMap()
        return try {
            val body = quranJson.encodeToString(QuranResolveRequestDto(references))
            val response = client.post("$baseUrl/api/quran/resolve") {
                contentType(ContentType.Application.Json)
                setBody(body)
            }
            if (response.status.isSuccess())
                quranJson.decodeFromString<QuranResolveResponseDto>(response.bodyAsText()).verses
            else {
                println("[QURAN-RESOLVE] Failed: ${response.status}")
                null
            }
        } catch (e: Exception) {
            println("[QURAN-RESOLVE] Error: ${e.message}")
            null
        }
    }

    /** Fetch a cached analysis result via HTTP. Used when the SSE 'complete' event
     *  only contains a lightweight signal (score/cost) instead of the full result. */
    /**
     * Why a POST /api/cached-result didn't produce a result. The endpoint refuses for several
     * genuinely different reasons — nothing cached yet, not purchased, auth rejected, server
     * fault, no answer at all — and collapsing them all to `null` is what made the field reports
     * of "could not fetch result from cache" impossible to act on.
     */
    sealed class CacheFetchOutcome {
        data class Success(val json: String) : CacheFetchOutcome()
        /** [reason] is a stable tag from [cacheFailureReason], safe to embed in an error string. */
        data class Failed(val reason: String) : CacheFetchOutcome()
    }

    /**
     * HTTP status → a short tag that says which refusal this was.
     *
     * Deliberately digit-free. MediaCheckerViewModel routes analysis errors by searching the
     * message for substrings like "402", "401" and "429" — a raw status code here would silently
     * reroute the error to the paywall / re-auth branch AND null out `internalError`, which is the
     * field the report email actually carries. Tags keep the detail and change no behaviour.
     */
    private fun cacheFailureReason(status: Int): String = when (status) {
        404 -> "not_found"          // no cached rows for this key (or Full asked, only Quick exists)
        402 -> "not_purchased"      // result exists, no matching charge for this user+criteria
        401 -> "auth_rejected"
        403 -> "forbidden"
        408, 504 -> "upstream_timeout"
        in 500..599 -> "server_error"
        else -> "unexpected_status"
    }

    /**
     * Fetch a cached result, reporting *why* on failure. See [fetchCachedResult] for the plain shim.
     *
     * @param forceFreshToken refresh the session BEFORE the first attempt instead of only reacting
     * to a 401. Set only on the post-`complete` collection path: that call trails /api/analyze by
     * minutes, which is long enough for the access token minted at the start of the journey to have
     * expired, and reacting after the fact costs an extra round trip on a request the user is
     * already staring at a spinner for. The shim callers (version sync, stream watchdog) deliberately
     * leave it false — they fire once per history title, and a forced refresh each time would mean
     * dozens of GoTrue round trips on app open.
     */
    suspend fun fetchCachedResultDetailed(
        requestBody: String,
        forceFreshToken: Boolean = false,
        /** Ask for a run still in progress: write-ups not finished yet come back "pending". */
        partial: Boolean = false,
    ): CacheFetchOutcome {
        val url = "$baseUrl/api/cached-result" + if (partial) "?partial=1" else ""
        return try {
            val token = if (forceFreshToken) getFreshAuthToken() ?: getAuthToken() else getAuthToken()
            var response = client.post(url) {
                contentType(ContentType.Application.Json)
                token?.let { header("Authorization", "Bearer $it") }
                setBody(requestBody)
            }

            // Refresh once on 401, exactly as postDisambiguate does. This call is the tail of a
            // long journey — /api/analyze mints a fresh token up front, but collection happens
            // minutes later, after the server's own 5-minute validation cache has lapsed and it
            // re-checks with Supabase. A token that was fine at the start can be dead here, and
            // the analysis has already run AND been billed by then (the refund path only fires
            // when the server never emits `complete`), so the user pays for a result they're
            // shown an error for. This was the sole authenticated call in this file reading the
            // cached token without a refresh, which is why it was the only one that surfaced it.
            //
            // Note the retry in fetchCachedResultRetrying cannot cover this: it spaces out
            // attempts to outlast a write-visibility race, but no amount of waiting revives an
            // expired credential — only a new token does. Refreshing here also fixes that loop,
            // since each later attempt re-reads the now-refreshed session.
            if (response.status.value == 401) {
                // getFreshAuthToken falls back to the CACHED token whenever refreshCurrentSession
                // fails, so it can hand back the very credential that was just rejected. Retry only
                // on a genuinely different one — re-posting the same token buys a second identical
                // 401 and a pointless round trip on an already-degraded connection.
                val fresh = getFreshAuthToken()
                if (fresh != null && fresh != token) {
                    println("[CACHE-FETCH] 401 — session refreshed, retrying once")
                    response = try {
                        client.post(url) {
                            contentType(ContentType.Application.Json)
                            header("Authorization", "Bearer $fresh")
                            setBody(requestBody)
                        }
                    } catch (e: Exception) {
                        // Keep the diagnosis we already have. Letting this reach the outer catch
                        // would report `no_response` and bury a CONFIRMED auth failure — and the
                        // reason tag is the whole point of this function (a bare "could not fetch
                        // result from cache" is what made these reports unactionable before).
                        println("[CACHE-FETCH] retry threw (${e.message}) — keeping auth_rejected")
                        return CacheFetchOutcome.Failed("auth_rejected")
                    }
                } else {
                    println("[CACHE-FETCH] 401 — refresh yielded no new token, not retrying")
                    // No usable credential at all — not the same failure as "the server looked at our
                    // token and said no", and the two need different advice (sign in again vs. retry).
                    // Distinguishing them here is what makes the next field report triageable from the
                    // reason tag alone. Digit-free, like every other tag: see [cacheFailureReason].
                    if (fresh == null && token == null) return CacheFetchOutcome.Failed("auth_no_session")
                }
            }

            if (response.status.isSuccess()) {
                CacheFetchOutcome.Success(response.bodyAsText())
            } else {
                // A kids read the server refused because KIDZONE isn't switched on for this account.
                // Told apart from every other 403 by its code, so the kids path can show "Ask a grown-up".
                val kidsNotAllowed = response.status.value == 403 &&
                    (try { response.bodyAsText() } catch (_: Exception) { "" }).contains(KIDS_NOT_ALLOWED_CODE)
                val reason = if (kidsNotAllowed) KIDS_NOT_ALLOWED_CODE else cacheFailureReason(response.status.value)
                println("[CACHE-FETCH] Failed: ${response.status} -> $reason")
                CacheFetchOutcome.Failed(reason)
            }
        } catch (e: Exception) {
            println("[CACHE-FETCH] Error: ${e.message}")
            CacheFetchOutcome.Failed("no_response")
        }
    }

    /**
     * Shim for callers that only need "did a result come back?" — the version sync and the stream
     * watchdog, both of which treat every failure as "nothing to do" and stay silent.
     */
    suspend fun fetchCachedResult(requestBody: String, partial: Boolean = false): String? =
        (fetchCachedResultDetailed(requestBody, partial = partial) as? CacheFetchOutcome.Success)?.json

    /** Batched, lightweight version probe (POST /api/analysis-versions). Body is
     *  {"items":[{title,mediaType,year?}]}; returns {"versions":[{title,mediaType,year?,version}]}.
     *  Used on app open to detect titles whose admin-edited analysis/cover changed so they can be
     *  re-fetched. Returns only public timestamps — no auth, no charge. Null on transport error. */
    suspend fun fetchAnalysisVersions(requestBody: String): String? {
        return try {
            val response = client.post("$baseUrl/api/analysis-versions") {
                contentType(ContentType.Application.Json)
                setBody(requestBody)
            }
            if (response.status.isSuccess()) response.bodyAsText() else null
        } catch (e: Exception) {
            println("[VERSION-SYNC] versions probe error: ${e.message}")
            null
        }
    }

    /**
     * V2: Retry a single failed Phase 2 unit. Used when the user taps the retry icon on a
     * concern card showing enrichmentStatus="failed". Returns the raw JSON response from the
     * server (status: "success" | "failed" | "precondition_missing"), or null on transport
     * error so the caller can surface a generic retry-failed message.
     */
    suspend fun retryConcern(requestBody: String): String? {
        return try {
            val token = getAuthToken()
            val response = client.post("$baseUrl/api/retry-concern") {
                contentType(ContentType.Application.Json)
                token?.let { header("Authorization", "Bearer $it") }
                setBody(requestBody)
            }
            if (response.status.isSuccess() || response.status.value == 404) {
                response.bodyAsText()
            } else {
                println("[RETRY-CONCERN] Failed: ${response.status}")
                null
            }
        } catch (e: Exception) {
            println("[RETRY-CONCERN] Error: ${e.message}")
            null
        }
    }

    // ── V1 endpoints (kept for backward compat, will be removed) ──
    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
        encodeDefaults = true
        explicitNulls = false
    }

    suspend fun sendMessage(request: ApiRequest): ApiResponse {
        val requestBody = json.encodeToString(ApiRequest.serializer(), request)
        val response = try {
            client.post("$baseUrl/api/proxy") {
                contentType(ContentType.Application.Json)
                setBody(requestBody)
            }
        } catch (e: Exception) {
            throw Exception("Cannot connect to server. The server may be starting up. Please try again in a moment.")
        }
        val responseText = response.bodyAsText()
        if (!response.status.isSuccess()) {
            throw Exception("Server error (${response.status}): $responseText")
        }
        if (responseText.trimStart().startsWith("<")) {
            throw Exception("Server returned an unexpected response. Please try again.")
        }
        // Parse manually to handle web search response format
        return parseResponse(responseText)
    }

    suspend fun sendPerplexitySearch(query: String, systemPrompt: String): String {
        val request = PerplexityRequest(
            messages = listOf(
                PerplexityMessage(role = "system", content = systemPrompt),
                PerplexityMessage(role = "user", content = query)
            )
        )
        val requestBody = json.encodeToString(PerplexityRequest.serializer(), request)
        val response = try {
            client.post("$baseUrl/api/perplexity") {
                contentType(ContentType.Application.Json)
                setBody(requestBody)
            }
        } catch (e: Exception) {
            throw Exception("Cannot connect to server. The server may be starting up. Please try again in a moment.")
        }
        val responseText = response.bodyAsText()
        if (!response.status.isSuccess()) {
            throw Exception("Perplexity error (${response.status}): $responseText")
        }
        val perplexityResponse = json.decodeFromString<PerplexityResponse>(responseText)
        return perplexityResponse.choices.firstOrNull()?.message?.content ?: ""
    }

    suspend fun sendFeedback(message: String, imageBytes: ByteArray? = null, imageName: String? = null, userEmail: String? = null, userName: String? = null): Boolean {
        return try {
            val version = try { "${com.islamicmedia.shared.util.appVersion} (${com.islamicmedia.shared.util.appBuildNumber})" } catch (_: Exception) { null }
            val platform = try { com.islamicmedia.shared.util.platformName } catch (_: Exception) { null }
            val response = client.submitFormWithBinaryData(
                url = "$baseUrl/api/feedback",
                formData = formData {
                    append("message", message)
                    if (!userEmail.isNullOrBlank()) append("userEmail", userEmail)
                    if (!userName.isNullOrBlank()) append("userName", userName)
                    if (version != null) append("appVersion", version)
                    if (platform != null) append("platform", platform)
                    if (imageBytes != null) {
                        append("image", imageBytes, Headers.build {
                            append(HttpHeaders.ContentType, "image/png")
                            append(HttpHeaders.ContentDisposition, "filename=\"${imageName ?: "screenshot.png"}\"")
                        })
                    }
                }
            )
            response.status.isSuccess()
        } catch (e: Exception) {
            println("[FEEDBACK] Failed to send: ${e.message}")
            false
        }
    }

    suspend fun sendErrorReport(userMessage: String, errorDetails: String, title: String, userEmail: String? = null): Boolean {
        return try {
            val version = try { "${com.islamicmedia.shared.util.appVersion} (${com.islamicmedia.shared.util.appBuildNumber})" } catch (_: Exception) { null }
            val platform = try { com.islamicmedia.shared.util.platformName } catch (_: Exception) { null }
            val body = json.encodeToString(
                ErrorReportRequest.serializer(),
                ErrorReportRequest(userMessage, errorDetails, title, userEmail, version, platform)
            )
            val response = client.post("$baseUrl/api/error-report") {
                contentType(ContentType.Application.Json)
                setBody(body)
            }
            response.status.isSuccess()
        } catch (e: Exception) {
            println("[ERROR-REPORT] Failed to send: ${e.message}")
            false
        }
    }

    /** Flag a problematic analysis. Stored server-side + emailed to the team; surfaced in the admin dashboard. */
    suspend fun submitFlag(
        title: String,
        mediaType: String?,
        score: Int?,
        quickCheck: Boolean,
        message: String,
        criteriaJson: String? = null,
    ): Boolean {
        return try {
            val version = try { "${com.islamicmedia.shared.util.appVersion} (${com.islamicmedia.shared.util.appBuildNumber})" } catch (_: Exception) { null }
            val platform = try { com.islamicmedia.shared.util.platformName } catch (_: Exception) { null }
            val authToken = getAuthToken()
            val email = try {
                com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.currentUser?.email
            } catch (_: Exception) { null }
            val body = json.encodeToString(
                FlagAnalysisRequest.serializer(),
                FlagAnalysisRequest(title, mediaType, score, quickCheck, message, criteriaJson, email, version, platform)
            )
            val response = client.post("$baseUrl/api/flag-analysis") {
                contentType(ContentType.Application.Json)
                authToken?.let { header("Authorization", "Bearer $it") }
                setBody(body)
            }
            response.status.isSuccess()
        } catch (e: Exception) {
            println("[FLAG] Failed to send: ${e.message}")
            false
        }
    }

    private fun parseResponse(responseText: String): ApiResponse {
        val root = json.parseToJsonElement(responseText).jsonObject
        val contentArray = root["content"]?.jsonArray ?: return ApiResponse()
        val blocks = contentArray.mapNotNull { element ->
            val obj = element.jsonObject
            val type = obj["type"]?.jsonPrimitive?.content ?: return@mapNotNull null
            val text = obj["text"]?.jsonPrimitive?.content
            ContentBlock(type = type, text = text)
        }
        val usage = try {
            root["usage"]?.let { json.decodeFromString<ApiUsage>(it.toString()) }
        } catch (_: Exception) { null }
        return ApiResponse(content = blocks, usage = usage)
    }
}
